import { ReactNode } from 'react';
import {
  Title,
  Text,
  Group,
  Box,
  useMantineTheme,
  rem,
  Stack,
  Divider,
  Flex,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { MobileBackButton } from './MobileBackButton';
import { DesktopBreadcrumbs } from './DesktopBreadcrumbs';

export interface PageTitleProps {
  /** Main page title */
  title: string;
  /** Optional subtitle or description */
  description?: string;
  /** Optional action buttons or components to display on the right */
  actions?: ReactNode;
  /** Optional breadcrumbs for navigation */
  breadcrumbs?: Array<{
    title: string;
    href?: string;
  }>;
  /** Optional icon to display next to the title */
  icon?: ReactNode;
  /** Whether to display a border on the bottom */
  withBorder?: boolean;
  /** Optional className for custom styling */
  className?: string;
  /** Size of the title */
  size?: 'sm' | 'md' | 'lg';
  /** Optional children to render below the title */
  children?: ReactNode;
  /** Enable compact mobile layout */
  compact?: boolean;
  /** Custom back button action (overrides default breadcrumb back) */
  onBack?: () => void;
  /** Custom back button text */
  backText?: string;
  /** Hide back button completely */
  hideBack?: boolean;
  /** Make the header sticky on mobile */
  sticky?: boolean;
  /** Background color for sticky header */
  backgroundColor?: string;
}

/**
 * Enhanced PageTitle component with mobile-first design
 * Features: Smart back navigation, responsive layout, sticky headers, and consistent styling
 */
export const PageTitle = ({
  title,
  description,
  actions,
  breadcrumbs,
  icon,
  withBorder = false,
  className,
  size = 'md',
  children,
  compact = false,
  onBack,
  backText,
  hideBack = false,
  sticky = false,
  backgroundColor = 'var(--mantine-color-body)'
}: PageTitleProps) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const sizeConfig = {
    sm: {
      order: 3 as const,
      desktop: '1.5rem',
      tablet: '1.3rem',
      mobile: '1.25rem',
      iconScale: 0.8
    },
    md: {
      order: 2 as const,
      desktop: '2rem',
      tablet: '1.8rem',
      mobile: '1.5rem',
      iconScale: 0.9
    },
    lg: {
      order: 1 as const,
      desktop: '2.5rem',
      tablet: '2.2rem',
      mobile: '1.8rem',
      iconScale: 1
    },
  };

  const config = sizeConfig[size];

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (breadcrumbs && breadcrumbs.length > 1) {
      const parentCrumb = breadcrumbs[breadcrumbs.length - 2];
      if (parentCrumb.href) {
        navigate(parentCrumb.href);
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const shouldShowBack = !hideBack && (Boolean(onBack) || (breadcrumbs && breadcrumbs.length > 1) || window.history.length > 1);
  const backButtonText = backText || (breadcrumbs && breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2].title : 'Back');

  const headerContent = (
    <Box
      pt={{ base: rem(16), sm: rem(24) }}
      className={className}
      style={{
        marginBottom: compact ? rem(12) : rem(24),
        paddingBottom: withBorder ? rem(16) : 0,
        borderBottom: withBorder ? `1px solid ${theme.colors.gray[3]}` : 'none',
      }}
    >
      <Stack gap={rem(12)}>
        <Box hiddenFrom="sm">
          <MobileBackButton
            shouldShow={shouldShowBack}
            onBack={handleBack}
            backText={backButtonText}
          />
        </Box>

        <Box visibleFrom="sm">
          <DesktopBreadcrumbs breadcrumbs={breadcrumbs} />
        </Box>

        <Flex
          direction={{ base: 'column', sm: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', sm: 'flex-start' }}
          gap={rem(16)}
        >
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group gap={rem(12)} align="center" wrap="nowrap">
              {icon && (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    transform: `scale(${config.iconScale})`,
                  }}
                  visibleFrom="sm"
                >
                  {icon}
                </Box>
              )}
              <Title
                order={config.order}
                style={{
                  fontSize: config.desktop,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  margin: 0,
                  [`@media (max-width: ${theme.breakpoints.lg})`]: {
                    fontSize: config.tablet
                  },
                  [`@media (max-width: ${theme.breakpoints.sm})`]: {
                    fontSize: config.mobile,
                    fontWeight: 600,
                    lineHeight: 1.3
                  }
                }}
              >
                {title}
              </Title>
            </Group>

            {description && (
              <Text
                c="dimmed"
                mt={rem(6)}
                style={{
                  fontSize: rem(15),
                  lineHeight: 1.5,
                  maxWidth: rem(600),
                  [`@media (max-width: ${theme.breakpoints.sm})`]: {
                    fontSize: rem(14),
                    lineHeight: 1.4,
                    marginTop: rem(4)
                  }
                }}
              >
                {description}
              </Text>
            )}
          </Box>

          {actions && (
            <Box
              style={{
                flexShrink: 0,
                [`@media (max-width: ${theme.breakpoints.sm})`]: {
                  width: '100%',
                  marginTop: rem(8)
                }
              }}
            >
              <Group
                gap={rem(8)}
                style={{
                  [`@media (max-width: ${theme.breakpoints.sm})`]: {
                    justifyContent: 'flex-start'
                  }
                }}
              >
                {actions}
              </Group>
            </Box>
          )}
        </Flex>

        {children && (
          <>
            {(description || actions) && (
              <Divider
                style={{
                  opacity: 0.3,
                  marginTop: rem(8),
                  marginBottom: rem(8)
                }}
              />
            )}
            <Box>
              {children}
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );

  if (sticky) {
    return (
      <Box
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor,
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
          [`@media (max-width: ${theme.breakpoints.sm})`]: {
            backgroundColor: theme.colors.gray[0],
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
          }
        }}
      >
        <Box p={{ base: rem(16), sm: rem(24) }}>
          {headerContent}
        </Box>
      </Box>
    );
  }

  return headerContent;
};

export default PageTitle;
